/**
 * RequestChangeModal — propose a change to one of an Orchist's date fields.
 *
 * Per Change Doctrine: every Orchist can propose a change to their own
 * dates. If they have an inviter (the typical case), the change becomes
 * a request awaiting that inviter's consent. If they're The Founding
 * Orchist (no inviter), the change applies directly + logs as 'self'.
 *
 * For MVP, the target is always the caller's OWN row. Future polish
 * (Phase G.7+) will let an inviter propose changes for invitees they
 * Acknowledged — same RPC, just different target_contact_id.
 *
 * Field options:
 *   - invitationSentAt          (only visible if non-null on caller's row)
 *   - invitationProposedAcceptedAt (only visible if non-null)
 *   - invitationAcceptedAt      (always available for any Orchist)
 */

import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { DatePicker } from '@/components/DatePicker';
import {
  classifyDateInput,
  localDateStringToUTC,
  utcToLocalDateString,
  resolveTimezone,
} from '@/lib/dates';
import type { ChangeRequestField } from '@/hooks/useChangeRequests';

export interface RequestChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const FIELD_LABELS: Record<ChangeRequestField, { title: string; hint: string }> = {
  invitationAcceptedAt: {
    title: 'When you remembered',
    hint: 'Your canonical "I knew" date — your acknowledgment moment.',
  },
  invitationProposedAcceptedAt: {
    title: 'When the inviter proposed you Acknowledged',
    hint: 'The inviter\'s proposed date for your Acknowledgment.',
  },
  invitationSentAt: {
    title: 'When the invitation was extended',
    hint: 'The conversation date — when the inviter said they told you.',
  },
};

export function RequestChangeModal({ isOpen, onClose, onCreated }: RequestChangeModalProps) {
  const { contact } = useAuth();
  const [field, setField] = useState<ChangeRequestField>('invitationAcceptedAt');
  const [proposedDate, setProposedDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{ kind: 'self_applied' | 'request_created'; expiresAt?: string } | null>(null);

  const tz = resolveTimezone(contact?.timezone);

  // Determine which fields are available based on contact state
  const availableFields: ChangeRequestField[] = (() => {
    if (!contact) return [];
    const fields: ChangeRequestField[] = ['invitationAcceptedAt'];
    if (contact.invitationProposedAcceptedAt) fields.push('invitationProposedAcceptedAt');
    if (contact.invitationSentAt) fields.push('invitationSentAt');
    return fields;
  })();

  // When opening, prefill proposed date with the current value of the chosen field
  useEffect(() => {
    if (!isOpen || !contact) return;
    setSubmitState('idle');
    setErrorMessage(null);
    setConfirmation(null);
    setReason('');

    // Read the actual current value of the selected field from the
    // Contact record (now that invitationAcceptedAt + invitationConfirmedAt
    // are part of ContactRow). For invitationAcceptedAt specifically, fall
    // back to invitationConfirmedAt as a sibling timestamp before defaulting
    // to today — older Orchist rows might only have one of the two set.
    let currentForField: string | null = null;
    if (field === 'invitationAcceptedAt') {
      currentForField =
        contact.invitationAcceptedAt ??
        contact.invitationConfirmedAt ??
        null;
    } else if (field === 'invitationProposedAcceptedAt') {
      currentForField = contact.invitationProposedAcceptedAt ?? null;
    } else {
      currentForField = contact.invitationSentAt ?? null;
    }

    if (currentForField) {
      setProposedDate(utcToLocalDateString(currentForField, tz));
    } else {
      setProposedDate(utcToLocalDateString(new Date().toISOString(), tz));
    }
  }, [isOpen, contact, field, tz]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !contact) return null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitState === 'submitting') return;

    const dateClass = classifyDateInput(proposedDate);
    if (dateClass === 'malformed') {
      setErrorMessage('Date must be in YYYY-MM-DD format.');
      return;
    }
    if (dateClass === 'out_of_range') {
      setErrorMessage('This date is beyond what The Line can record. Pick something within human-representable time, or use the "beyond all calendars" option.');
      return;
    }

    const proposedTs = localDateStringToUTC(proposedDate, tz);
    if (proposedTs === null) {
      setErrorMessage('Could not interpret the date.');
      return;
    }

    setSubmitState('submitting');
    setErrorMessage(null);

    try {
      const { data, error } = await supabase.schema('core').rpc('request_date_change', {
        p_target_contact_id: contact!.id,
        p_field: field,
        p_proposed_value: proposedTs,
        p_reason: reason.trim() || null,
      });

      if (error) throw new Error(error.message);

      const result = data as { kind: 'self_applied' | 'request_created'; expires_at?: string };
      setConfirmation({
        kind: result.kind,
        expiresAt: result.expires_at,
      });
      setSubmitState('idle');
      onCreated?.();
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Submission failed.');
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-lg bg-paper border border-ink/20 shadow-xl p-8 space-y-7 max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            Renew the date
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">Request a change.</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-sm mx-auto leading-relaxed">
            Change is inevitable. Propose the truth as you remember it now.
          </p>
        </div>

        {confirmation ? (
          <ConfirmationView
            kind={confirmation.kind}
            expiresAt={confirmation.expiresAt}
            onClose={() => {
              setConfirmation(null);
              onClose();
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Field selector */}
            <fieldset className="space-y-3">
              <legend className="text-sm tracking-wide uppercase text-ink/60 mb-2">
                Which date?
              </legend>
              {availableFields.map((f) => (
                <label
                  key={f}
                  className={`block border p-3 cursor-pointer transition-colors ${
                    field === f
                      ? 'border-ink/60 bg-paper/80'
                      : 'border-ink/15 hover:border-ink/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="request-field"
                    value={f}
                    checked={field === f}
                    onChange={() => setField(f)}
                    className="sr-only"
                  />
                  <div className="display text-sm font-medium">{FIELD_LABELS[f].title}</div>
                  <div className="text-xs text-ink/55 italic">{FIELD_LABELS[f].hint}</div>
                </label>
              ))}
            </fieldset>

            {/* Proposed date */}
            <div className="space-y-2">
              <label
                htmlFor="proposed-date-change"
                className="text-sm tracking-wide uppercase text-ink/60"
              >
                New date
              </label>
              <DatePicker
                id="proposed-date-change"
                value={proposedDate}
                onChange={setProposedDate}
                disabled={submitState === 'submitting'}
                ariaLabel="Proposed date"
              />
            </div>

            {/* Optional reason */}
            <div className="space-y-2">
              <label
                htmlFor="reason"
                className="text-sm tracking-wide uppercase text-ink/60"
              >
                Reason{' '}
                <span className="lowercase italic tracking-normal text-ink/40">
                  (optional, but a kindness)
                </span>
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitState === 'submitting'}
                maxLength={500}
                rows={2}
                placeholder="Help the other party understand why."
                className="w-full px-4 py-3 border border-ink/20 bg-paper/50 text-sm font-display italic placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitState === 'submitting'}
                className="flex-1 px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitState === 'submitting'}
                className="flex-1 px-5 py-3 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitState === 'submitting' ? 'Sending...' : 'Propose'}
              </button>
            </div>

            {errorMessage && (
              <p className="text-sm text-destructive text-center" role="alert">
                {errorMessage}
              </p>
            )}

            <p className="text-[0.7rem] text-ink/40 italic font-display text-center pt-1 leading-relaxed">
              The other party will see your proposal on their home surface
              <strong className="not-italic"> and receive an email</strong>.
              They have 30 days to respond. If they don't, the cosmos accepts.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function ConfirmationView({
  kind,
  expiresAt,
  onClose,
}: {
  kind: 'self_applied' | 'request_created';
  expiresAt?: string;
  onClose: () => void;
}) {
  return (
    <div className="text-center space-y-5 py-2">
      {kind === 'self_applied' ? (
        <>
          <p className="display italic text-2xl text-accent">It is so.</p>
          <p className="text-sm text-ink/65 italic font-display leading-relaxed max-w-sm mx-auto">
            Your record is updated. The trail is preserved in the audit log.
          </p>
        </>
      ) : (
        <>
          <p className="display italic text-2xl text-accent">The proposal travels.</p>
          <p className="text-sm text-ink/65 italic font-display leading-relaxed max-w-sm mx-auto">
            The other party will see it on their home surface, and an email
            in The Line's voice will land in their inbox. They have 30 days
            to respond. If they don't, the cosmos accepts.
          </p>
          {expiresAt && (
            <p className="text-xs text-ink/40 italic">
              Expires {new Date(expiresAt).toLocaleDateString()}.
            </p>
          )}
        </>
      )}
      <button
        type="button"
        onClick={onClose}
        className="px-6 py-3 border border-ink/30 text-ink/80 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
      >
        Close
      </button>
    </div>
  );
}
